import type { TalkJSData } from "../chat.types";
import type { Circle } from "@core/types/core.types";

export type IsChatEnabledArgs = {
  /** TalkJS data from the server */
  talkjsData: TalkJSData | null;
  /** User's circles */
  userCircles: Circle[] | null | undefined;
};

/**
 * Determines if chat is enabled based on TalkJS config and user circles.
 *
 * @example
 * ```typescript
 * const enabled = isChatEnabled({
 *   talkjsData,
 *   userCircles: user?.circles,
 * });
 * ```
 */
export function isChatEnabled({
  talkjsData,
  userCircles,
}: IsChatEnabledArgs): boolean {
  if (!talkjsData) return false;

  const { chat_status, chat_circle_ids } = talkjsData;

  // If status is "off", chat is disabled for everyone
  if (chat_status === "off") return false;

  // If status is "on", chat is enabled for everyone
  if (chat_status === "on") return true;

  // Parse comma-separated circle IDs and convert to integers
  const configuredIds = chat_circle_ids
    ? chat_circle_ids
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id))
    : [];

  // Get user's circle IDs and convert to integers
  const userCircleIds =
    userCircles
      ?.map((circle) =>
        typeof circle.id === "string" ? parseInt(circle.id) : circle.id
      )
      .filter((id) => !isNaN(id)) || [];

  // If status is "exclude", check if any circle IDs are in the exclude list
  if (chat_status === "exclude") {
    return !userCircleIds.some((circleId) => configuredIds.includes(circleId));
  }

  // If status is "include", check if any circle IDs are in the include list
  if (chat_status === "include") {
    return userCircleIds.some((circleId) => configuredIds.includes(circleId));
  }

  // Default to enabled if status is unknown
  return true;
}

export default isChatEnabled;

